import { NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import {
  SquaresFour, Television, Stack, FilmSlate, CalendarBlank,
  Broadcast, Pulse, GearSix, SignOut, CircleNotch, ClipboardText, Info, ShieldCheck,
} from "@phosphor-icons/react";

const NAV = [
  { to: "/platform", label: "Platform Admin", icon: ShieldCheck, platformOnly: true },
  { to: "/syndication", label: "Syndication", icon: Broadcast, platformOnly: true },
  { to: "/", label: "Dashboard", icon: SquaresFour, end: true },
  { to: "/channels", label: "Channels", icon: Television },
  { to: "/programming", label: "Programming", icon: Stack },
  { to: "/assets", label: "Asset Library", icon: FilmSlate },
  { to: "/scheduler", label: "Scheduler", icon: CalendarBlank },
  { to: "/outputs", label: "Outputs", icon: Broadcast },
  { to: "/monitoring", label: "Monitoring", icon: Pulse },
  { to: "/settings", label: "Settings", icon: GearSix },
  { to: "/audit", label: "Audit Log", icon: ClipboardText, roles: ["owner", "admin"] },
  { to: "/system", label: "System", icon: Info },
];

export function Layout({ children }) {
  const { user, org, logout } = useAuth();
  const navigate = useNavigate();
  const nav = NAV.filter((i) => {
    if (i.platformOnly) return user?.is_platform_admin === true;
    if (i.roles) return i.roles.includes(user?.role);
    return true;
  });

  return (
    <div className="flex min-h-screen bg-[#0A0A0A] text-white">
      <aside className="w-60 shrink-0 border-r border-[#262626] flex flex-col fixed h-screen z-30 bg-[#0A0A0A]">
        <div className="h-16 flex items-center gap-2.5 px-5 border-b border-[#262626]">
          <div className="h-7 w-7 bg-[#3A86FF] flex items-center justify-center rounded-sm">
            <Broadcast size={18} weight="fill" className="text-white" />
          </div>
          <div className="leading-none">
            <div className="font-head font-black text-sm tracking-tight">CHANNEL</div>
            <div className="font-mono text-[9px] tracking-[0.3em] text-[#737373]">COMMAND STUDIO</div>
          </div>
        </div>

        <nav className="flex-1 py-3 overflow-y-auto">
          {nav.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              data-testid={`nav-${item.label.toLowerCase()}`}
              className={({ isActive }) =>
                `flex items-center gap-3 px-5 py-2 text-sm border-l-2 transition-colors duration-150 ${
                  isActive
                    ? "border-[#3A86FF] bg-[#141414] text-white"
                    : "border-transparent text-[#A3A3A3] hover:bg-[#141414] hover:text-white"
                }`
              }
            >
              <item.icon size={18} weight="regular" />
              {item.label}
            </NavLink>
          ))}
        </nav>

        <div className="border-t border-[#262626] p-4">
          <div className="text-[10px] uppercase tracking-[0.2em] text-[#737373] mb-1">Organization</div>
          <div className="font-head text-sm truncate mb-3">{org?.name || "—"}</div>
          <div className="flex items-center justify-between">
            <div className="min-w-0">
              <div className="text-xs text-white truncate">{user?.name}</div>
              <div className="text-[10px] text-[#737373] uppercase tracking-wider">{user?.role}</div>
            </div>
            <button
              data-testid="logout-btn"
              onClick={() => { logout(); navigate("/login"); }}
              className="text-[#A3A3A3] hover:text-red-500 transition-colors duration-150 p-1.5"
              title="Sign out"
            >
              <SignOut size={18} />
            </button>
          </div>
        </div>
      </aside>

      <main className="flex-1 ml-60 min-h-screen">{children}</main>
    </div>
  );
}

export function PageHeader({ title, subtitle, actions }) {
  return (
    <div className="h-16 border-b border-[#262626] flex items-center justify-between px-8 sticky top-0 bg-[#0A0A0A]/95 backdrop-blur z-20">
      <div>
        <h1 className="font-head font-bold text-xl tracking-tight leading-none">{title}</h1>
        {subtitle && <div className="text-xs text-[#737373] mt-1">{subtitle}</div>}
      </div>
      <div className="flex items-center gap-3">{actions}</div>
    </div>
  );
}

export function Loading() {
  return (
    <div className="flex items-center justify-center h-64 text-[#737373]">
      <CircleNotch size={28} className="animate-spin" />
    </div>
  );
}
